import type { LocaleCode } from '@/api/models';

/**
 * The locale to fall back to when the requested one is missing. Mirrors the
 * app's i18next `fallbackLng` ('en').
 */
export const DEFAULT_ACCOUNT_LOCALE: LocaleCode = 'en';

type NamedAccount = {
    name: Partial<Record<LocaleCode, string>>;
    code: string;
};

/**
 * Resolves an account's display name via the fallback chain:
 * requested locale → default locale → first available name → `code`.
 *
 * Account `name` maps can be sparse (not every account carries every locale),
 * so this guarantees a non-empty string for the grid. Empty strings are treated
 * as absent so a blank localization doesn't win over a populated fallback.
 */
export const accountName = (
    account: NamedAccount,
    locale: LocaleCode,
    defaultLocale: LocaleCode = DEFAULT_ACCOUNT_LOCALE,
): string => {
    const names = account.name;

    const requested = names[locale];
    if (requested) {
        return requested;
    }

    const fallback = names[defaultLocale];
    if (fallback) {
        return fallback;
    }

    const firstAvailable = Object.values(names).find((value): value is string =>
        Boolean(value),
    );
    if (firstAvailable) {
        return firstAvailable;
    }

    return account.code;
};
